
import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { TogglePanelComponent, TogglePanelComponent as TogglePanelComponent_1 } from "@components/toggle-panel/toggle-panel.component";
import { isNil, sortBy } from "lodash";
import moment from "moment";
import { BehaviorSubject, Subject, combineLatest, from } from "rxjs";
import {
    filter,
    map,
    switchMap,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";

import { VirkeskopKontraktstatus } from "~app/common/models/constants";
import { Period } from "~app/common/models/period";
import { ObjectUtils } from "~app/common/utils/object-utils";
import {
    IFordelaLeveransplanQuery,
    IFordelaLeveransplanResponse,
    Leveransplan,
    Leveransvirkessplan,
    VirkeskostnadDataRow
} from "~app/features/virkeskop/models/leveransvirkesplan-models";
import { VKAtgardLeveransvirkesvolymDTO } from "../../models/atgard-leveransvirkesvolym-models";
import { VKLeveransplanNumericHotCell, VKNumericHotCell } from "../../models/hotdoc/vk-numeric.hot-cell";
import { Virkeskostnad, Virkeskostnader } from "../../models/virkeskostnader/virkeskostnader-models";
import { AvtalService } from "../../services/avtal.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { LeveransvirkesplanMediatorService } from "../../services/leveransvirkesplan-mediator.service";
import { LeveransvirkesvolymMediatorService } from "../../services/leveransvirkesvolym-mediator.service";
import { VirkeskostnaderMediatorService } from "../../services/virkeskostnader-mediator.service";

interface LeveransvirkesplanCellChangeArgs {
    plan: Leveransplan;
    change: NumericCellChangeArg;
    isNewPlan: boolean;
}

@Component({
    selector: "rask-leveransvirkesplan",
    templateUrl: "./leveransvirkesplan.component.html",
    styleUrls: ["./leveransvirkesplan.component.scss"],
    providers: [LeveransvirkesplanMediatorService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent_1,
        ButtonComponent,
        MessagePanelComponent,
        HotDocComponent,
        AsyncPipe
    ]
})
export class LeveransvirkesplanComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(TogglePanelComponent) togglePanel: TogglePanelComponent;
    hotDoc: HotDoc;
    noOfPeriodColumns = 0;
    periodSpan: moment.Moment[] = [];
    isDirty: boolean;
    infoMessages: IMessageModel[] = [];

    private readonly avtalService = inject(AvtalService);
    private readonly leveransvirkesvolymMediatorService = inject(LeveransvirkesvolymMediatorService);
    private readonly leveransvirkesplanMediatorService = inject(LeveransvirkesplanMediatorService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngZone = inject(NgZone);

    get leveransvirkesplan$() {
        return this.leveransvirkesplanMediatorService.vkLeveransvirkesplan$;
    }
    get virkeskostnader$() {
        return this.virkeskostnaderMediatorService.virkeskostnader$.pipe(
            filter((virkeskostnaderEvent) =>
                ObjectUtils.hasValue(virkeskostnaderEvent)));
    }

    get messages$() {
        return this.messagesSrc.asObservable();
    }

    private readonly messagesSrc = new BehaviorSubject<IMessageModel[]>([]);
    private readonly onCellChangeSrc = new Subject<LeveransvirkesplanCellChangeArgs>();
    private readonly destroy$ = new Subject<boolean>();

    private readonly fordelaVolymerRequest$ = new BehaviorSubject(null);

    ngAfterViewInit(): void {
        combineLatest([
            this.leveransvirkesplan$,
            this.virkeskostnader$,
            from(this.togglePanel.onToggle)
        ]
        ).pipe(
            takeUntil(this.destroy$),
            filter(([
                leveransvirkesplan,
                virkeskostnaderEvent,
                expanded
            ]) => expanded
                && leveransvirkesplan.leveransplanHandelssortiment?.length > 0
                && leveransvirkesplan.isPeriodValid()
                && virkeskostnaderEvent.virkeskostnader.kontraktVirkeskostnader?.length > 0)
        ).subscribe(() => {
            this.rebind();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    ngOnInit(): void {
        const fordelaVolymer$ = this.fordelaVolymerRequest$.pipe(
            withLatestFrom(this.leveransvirkesvolymMediatorService.leveransvirkesvolymer$, this.leveransvirkesplan$, this.virkeskostnader$),
            map(([
                _,
                volymer,
                leveransvirkesplan,
                virkeskostnader
            ]) => this.createFordelaVolymerQuery(leveransvirkesplan, virkeskostnader.virkeskostnader, volymer)),
            switchMap(query => this.avtalService.fordelaLeveransplan(query))
        );
        fordelaVolymer$.pipe(
            takeUntil(this.destroy$),
            withLatestFrom(this.leveransvirkesplan$, this.virkeskostnader$))
            .subscribe(([
                fordelaVolymerResponse,
                leveransvirkesplan,
                virkeskostnader
            ]) =>
                this.fordelaVolymer(leveransvirkesplan, virkeskostnader.virkeskostnader, fordelaVolymerResponse));

        combineLatest([
            this.leveransvirkesplan$,
            this.virkeskostnader$
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([
                leveransvirkesplan,
                virkeskostnaderEvent
            ]) => {
                this.initTable(leveransvirkesplan, this.getVirkeskostnader(virkeskostnaderEvent?.virkeskostnader));
                this.handleMessages(leveransvirkesplan, virkeskostnaderEvent?.virkeskostnader);
            });

        this.virkeskostnaderMediatorService.virkeskostnadDeleted$.pipe(
            map(virkeskostnadEvent => virkeskostnadEvent.virkeskostnad),
            withLatestFrom(this.leveransvirkesplan$),
            takeUntil(this.destroy$)
        ).subscribe(([
            virkeskostnad,
            leveransvirkesplan
        ]) => this.onVirkeskostnadRemoved(virkeskostnad, leveransvirkesplan));

        this.onCellChangeSrc.pipe(
            takeUntil(this.destroy$),
            withLatestFrom(this.leveransvirkesplan$, this.virkeskostnader$)
        ).subscribe(([
            args,
            leveransvirkesplan,
            virkeskostnaderEvent
        ]) => {
            this.onCellEditing(args, leveransvirkesplan, this.getVirkeskostnader(virkeskostnaderEvent?.virkeskostnader));
        });
    }

    onVirkeskostnadRemoved(virkeskostnad: Virkeskostnad, leveransvirkesplan: Leveransvirkessplan) {
        const leveransplanHandelssortiment = leveransvirkesplan.leveransplanHandelssortiment
            .find(lphs => lphs.virkeskostnadGUID === virkeskostnad.guid);

        if (!leveransplanHandelssortiment) {
            return;
        }

        leveransplanHandelssortiment.leveransplan
            .forEach(plan => this.leveransvirkesplanMediatorService.removeLeveransvirkesplan(plan));
    }

    fordelaVolymer(leveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnader, fordelaLeveransplanResponse: IFordelaLeveransplanResponse) {
        for (const hs of fordelaLeveransplanResponse.leveransplanHandelssortiment) {
            const virkeskostnad = this.getVirkeskostnader(virkeskostnader).find(vks =>
                (ObjectUtils.hasValue(vks.handelssortimentId) && vks.handelssortimentId === hs.handelssortimentId) ||
                (!ObjectUtils.hasValue(vks.handelssortimentId) &&
                    !ObjectUtils.hasValue(hs.handelssortimentId)
                    && vks.handelssortimentsgruppId === hs.handelssortimentsgruppId));

            const leveransplanHandelssortiment = leveransvirkesplan.leveransplanHandelssortiment
                .find(lphs => (ObjectUtils.hasValue(lphs.handelssortimentId) && lphs.handelssortimentId === hs.handelssortimentId) ||
                    (ObjectUtils.hasValue(lphs.handelssortimentGruppId) &&
                        !ObjectUtils.hasValue(lphs.handelssortimentId) &&
                        !ObjectUtils.hasValue(hs.handelssortimentId) &&
                        lphs.handelssortimentGruppId === hs.handelssortimentsgruppId));

            const newPerioder = hs.volymer.map(x => moment(x.period).format("MMM YY"));
            const perioderToRemove = leveransplanHandelssortiment.leveransplan
                .filter(x => !newPerioder.some(existingPeriod => existingPeriod === moment(x.period).format("MMM YY")));

            perioderToRemove.forEach(leveransplan => {
                const index = leveransplanHandelssortiment.leveransplan.indexOf(leveransplan);
                leveransplanHandelssortiment.leveransplan.splice(index, 1);
                this.leveransvirkesplanMediatorService.removeLeveransvirkesplan(leveransplan);
            });

            for (const volym of hs.volymer) {
                const period = this.periodSpan.find(p => p.format("MMM YY") === moment(volym.period).format("MMM YY"));

                let leveransplan = leveransplanHandelssortiment.leveransplan
                    .find(lp => moment(lp.period).format("MMM YY") === period.format("MMM YY"));

                if (!leveransplan) {
                    leveransplan = {
                        id: 0,
                        kvantitet: volym.kvantitet,
                        period: period.toDate(),
                        virkeskostnadId: virkeskostnad.id,
                        virkeskostnadGUID: virkeskostnad.guid
                    };

                    leveransplanHandelssortiment.leveransplan.push(leveransplan);

                    this.leveransvirkesplanMediatorService.createLeveransvirkesplan(leveransplan);
                } else {
                    leveransplan.kvantitet = volym.kvantitet;
                    this.leveransvirkesplanMediatorService.updateLeveransvirkesplan(leveransplan);
                }
            }
        }
        this.rebind();
    }

    onfordelaLeveransplanClick(): void {
        this.fordelaVolymerRequest$.next(null);
    }

    createFordelaVolymerQuery(leveransplan: Leveransvirkessplan, virkeskostnader: Virkeskostnader, volymer: VKAtgardLeveransvirkesvolymDTO[]): IFordelaLeveransplanQuery {
        const atgarder = leveransplan.atgarderForeslagetProduktionsdatum
            .map((period) => ({
                produktionsdatumFrom: moment(period.periodFrom).format("YYYY-MM-DD"),
                produktionsdatumTom: moment(period.periodTom).format("YYYY-MM-DD"),
                volymer: this.getVirkeskostnader(virkeskostnader).map(virkeskostnad => ({
                    handelssortimentsgruppId: virkeskostnad.handelssortimentsgruppId,
                    handelssortimentId: virkeskostnad.handelssortimentId,
                    kvantitet: volymer.find(vol => vol.virkeskostnadGuid === virkeskostnad.guid && vol.virkeskopsatgardLeveransvirkeGuid === period.guid)?.kvantitet
                }))
            }));

        return {
            atgarder: atgarder
        };
    }

    rebind() {
        this.ngZone.run(() => {
            this.hotDoc?.rebind();
        });
    }

    getRowKvantitet(virkeskostnadGuid: string, leveransplan: Leveransvirkessplan): number {
        return leveransplan.leveransplanHandelssortiment
            .find(x => x.virkeskostnadGUID === virkeskostnadGuid)?.leveransplan?.reduce((sum, plan) => sum + plan.kvantitet, 0);
    }

    isPeriodValid() {
        const kontrakt = this.kontraktMediatorService.kontrakt;
        const isAnbud = kontrakt.status === VirkeskopKontraktstatus.Anbud;
        const fromDate = moment(isAnbud ? kontrakt.anbudGiltighetFrom : kontrakt.kontraktGiltigtFrom);
        const tomDate = moment(isAnbud ? kontrakt.anbudGiltighetTom : kontrakt.kontraktGiltigtTom);
        if (!fromDate.isValid() || !tomDate.isValid()) {
            return false;
        }
        return fromDate.isBefore(tomDate);
    }

    handleMessages(vkLeveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnader): void {
        let message: IMessageModel;

        this.clearMessages();
        if (!this.isPeriodValid()) {
            message = {
                severity: MessageSeverity.Info,
                message: "Det krävs en giltig period på kontraktet för att kunna skapa leveransvirkesplan."
            } as IMessageModel;
            this.addMessage(message);
        }
        if (!virkeskostnader?.hasHandelssortiment()) {
            message = {
                severity: MessageSeverity.Info,
                message: "Du behöver lägga till handelssortiment i förkalkylen för att göra en leveransvirkesplan."
            } as IMessageModel;
            this.addMessage(message);
        }
        if (!vkLeveransvirkesplan?.canFordelaLeveransplan()) {
            message = {
                severity: MessageSeverity.Info,
                message: "Det finns inga åtgärder eller så finns åtgärder som saknar produktionsdatum och det går därför inte att fördela volymer."
            } as IMessageModel;
            this.addMessage(message);
        }

        this.showMessages();
    }

    addMessage(message: IMessageModel): void {
        this.infoMessages.push(message);
    }

    showMessages(): void {
        if (this.infoMessages.length > 0) {
            this.messagesSrc.next(this.infoMessages);
        }
    }

    clearMessages(): void {
        this.infoMessages.splice(0);
        this.messagesSrc.next([]);
    }

    parseFloatWithFormat(value: string) {
        const num = value.replace(/,/g, ".");
        return parseFloat(num);
    }

    initTable(vkLeveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnad[]): void {
        if ((virkeskostnader?.length ?? 0) === 0) {
            this.hotDoc = null;
            return; // hotdoc genererar konsolfel om man har en tabell utan rader ...
        }

        this.initMatrisSettings(vkLeveransvirkesplan, virkeskostnader);
    }

    initMatrisSettings(vkLeveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnad[]): void {
        const colFactory = () => this.getColumns(vkLeveransvirkesplan);
        const rowFactory = (doc: HotDoc) => this.getRows(doc, vkLeveransvirkesplan, virkeskostnader);
        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                calculateSum: false,
                numericParser: (value) => this.parseFloatWithFormat(value)
            },
            fixedColumnsStart: 2,
            stretchH: "all"
        };
        this.hotDoc = new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    }

    getColumns = (vkLeveransvirkesplan: Leveransvirkessplan): IHotColumnDefinition[] => {
        const period = vkLeveransvirkesplan ? vkLeveransvirkesplan.getAnbudKontraktPeriod() : new Period();
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "Handelssortiment",
            cellName: "handelssortiment",
            readOnly: true
        });
        columns.push({
            header: "",
            cellName: "mattslag",
            readOnly: true
        });
        this.noOfPeriodColumns = 0;
        this.periodSpan?.splice(0);
        for (let current = moment(period.periodFrom).startOf("month"); current <= moment(period.periodTom); current = moment(current).add(1, "month")) {
            const periodColumn = {
                header: current.format("MMM YY"),
                cellName: `period_${this.noOfPeriodColumns}`,
                readOnly: false,
                type: "numeric"
            };
            columns.push(periodColumn);

            this.periodSpan.push(current);
            this.noOfPeriodColumns = this.noOfPeriodColumns + 1;
        }
        columns.push({
            header: "Totalt",
            cellName: "summaTotalt",
            type: "numeric",
            readOnly: true
        });
        columns.push({
            header: "Förkalkyl",
            cellName: "summaForkalkyl",
            type: "numeric",
            readOnly: true
        });
        return columns;
    };

    getRows(doc: HotDoc, leveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnad[]) {
        if (!isNil(virkeskostnader)) {
            const leveransvirkeskostnader = sortBy(
                virkeskostnader,
                [
                    p => p.handelssortimentsgruppSekvensnummer,
                    p => p.handelssortimentId > 0,
                    p => p.handelssortimentNamn?.toLowerCase()
                ]
            ).map(vk => {
                const leveransplan = leveransvirkesplan.findOrCreateLeveransplaner(vk);

                return {
                    handelssortimentNamn: vk.handelssortimentNamn,
                    handelssortimentsgruppNamn: vk.handelssortimentsgruppNamn,
                    id: vk.id,
                    guid: vk.guid,
                    mattslag: vk.mattslag,
                    summaTotalt: this.getRowKvantitet(vk.guid, leveransvirkesplan),
                    summaForkalkyl: vk.kvantitet,
                    leveransplan: leveransplan
                };
            });

            leveransvirkeskostnader?.forEach(virkeskostnadRow => {
                this.createRowHS(doc, virkeskostnadRow);
            });
        }
    }

    createRowHS(doc: HotDoc, virkeskostnadRow: VirkeskostnadDataRow) {
        doc.createRow(row => {
            row.addText("handelssortiment", virkeskostnadRow.handelssortimentNamn ?? virkeskostnadRow.handelssortimentsgruppNamn, Cellstyle.Readonly);
            row.addText("mattslag", virkeskostnadRow.mattslag, Cellstyle.Readonly);
            //Perioder
            for (let i = 0; i < this.periodSpan.length; i++) {
                const levplan = virkeskostnadRow.leveransplan.find(x => moment(x.period).format("MMM YY") === this.periodSpan[i].format("MMM YY"));
                this.createCell(`period_${i}`, row, levplan ??
                    {
                        period: this.periodSpan[i].toDate(),
                        virkeskostnadId: virkeskostnadRow.id,
                        virkeskostnadGUID: virkeskostnadRow.guid
                    } as Leveransplan,
                ObjectUtils.isNullOrUndefined(levplan));
            }
            row.addNumeric("summaTotalt", virkeskostnadRow.summaTotalt, Cellstyle.Readonly);
            row.addNumeric("summaForkalkyl", virkeskostnadRow.summaForkalkyl, Cellstyle.Readonly);
        });
    }

    createCell(propertyName: string, row: HotRow, plan: Leveransplan, isNewPlan: boolean): VKNumericHotCell {
        const value = plan.kvantitet;
        const onchange = (cellchangeArg: any) => this.onCellChangeSrc.next({
            plan,
            change: cellchangeArg,
            isNewPlan
        });
        const flags: Cellstyle = Cellstyle.Default;
        const cell = new VKLeveransplanNumericHotCell(value, null, flags, onchange);
        return row.add(propertyName, cell);
    }

    onCellEditing(args: LeveransvirkesplanCellChangeArgs, leveransvirkesplan: Leveransvirkessplan, virkeskostnader: Virkeskostnad[]): void {
        const {
            plan, change, isNewPlan
        } = args;
        const isDelete: boolean = change.rawNewValue === null || change.rawNewValue === "0" || change.rawNewValue === "";
        const newkvantitet = change.newValue;

        plan.kvantitet = newkvantitet;

        const planer = leveransvirkesplan.leveransplanHandelssortiment
            .find(lphs => lphs.virkeskostnadGUID === plan.virkeskostnadGUID).leveransplan;

        if (isDelete) {
            const planToDelete = planer.findIndex(x => x.period === plan.period);
            if (planToDelete !== -1) {
                planer.splice(planToDelete, 1);
                this.leveransvirkesplanMediatorService.removeLeveransvirkesplan(plan);
            }
        } else if (isNewPlan) {
            planer.push(plan);
            this.leveransvirkesplanMediatorService.createLeveransvirkesplan(plan);
        } else {
            this.leveransvirkesplanMediatorService.updateLeveransvirkesplan(plan);
        }

        this.rebind();
        change.complete();
    }

    private getVirkeskostnader(virkeskostnader: Virkeskostnader, isLeveransvirkeU3 = true): Virkeskostnad[] {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (isLeveransvirkeU3) {
            return virkeskostnader?.kontraktVirkeskostnader;
        } else {
            return virkeskostnader.getForAllaAtgarder();
        }
    }
}
